import dayjs from 'dayjs';
import { RootState, store } from 'redux/store';
import { isOwner, ROLE } from './isOwner';
import { parseJWT } from 'api/apiClient';
import { AuthData } from 'redux/slices/auth/authSlice';
import { RequestMoneyDetailTypes } from 'redux/slices/sendAndRequest/requestMoneyDetailSlice';
import { FilterTransactionDetails, TransactionDetail } from 'redux/slices/dashboard/accountActivitySlice';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { deviceIdKey } from 'appConfig';
import Papa from 'papaparse';
import { ParseCSVResponse } from 'components/CSV/ExportCSVTable';

const enum TransactionTypes {
    TRANSFER_TYPE = 'TRANSFER',
}
interface Attribute {
    key?: string;
    name?: string;
    value?: string;
}

type DateTime = string | Date;
type Unit = 'date' | 'time';
type DateCombinationResult = string | null;
type StringAnyRecord = Record<string, any>;

type MerchantProfileItem = {
    key: string;
    value: string;
};

type TransactionType = 'TRANSFER' | 'M2M_PURCHASE' | string | undefined;

const validTimeFormats = ['hh:mm:ss A', 'HH:mm:ss.SSS'];

export const dateFormat = (date: DateTime, format?: string) => {
    if (!date) return '';
    return dayjs(date).format(format || 'MM/DD/YYYY');
};
export const timeFormat = (date: DateTime, format: string = 'hh:mm:ss A') => {
    if (!date) return '';
    if (dayjs(date).isValid()) {
        return dayjs(date).format(format);
    } else if (dayjs(date, validTimeFormats)) {
        return dayjs(date, validTimeFormats).format(format);
    }
    return 'Invalid Date';
};
export const convertToISOString = (date: DateTime): string => {
    if (isNaN(dayjs(date).valueOf())) {
        return dayjs().toISOString();
    }
    return dayjs(date).toISOString();
};

export const convertToDateObject = (date: DateTime): Date => {
    if (isNaN(dayjs(date).valueOf())) {
        return new Date();
    }
    return dayjs(date).toDate();
};

// compare with current date if second argument is omitted
export const isDateBefore = (date1: DateTime, date2?: DateTime) => {
    const isoDate1 = convertToISOString(date1);
    const isoDate2 = convertToISOString(date2 || '');
    if (!isoDate1 || !isoDate2) return null;
    return dayjs(isoDate1).isBefore(isoDate2);
};

// compare with current date if second argument is omitted
export const isDateAfter = (date1: DateTime, date2?: DateTime) => {
    const isoDate1 = convertToISOString(date1);
    const isoDate2 = convertToISOString(date2 || '');
    if (!isoDate1) return null;
    return dayjs(isoDate1).isAfter(isoDate2);
};

const combineDateTime = (orgDateTime: DateTime, newDateTime: DateTime, unit: Unit): DateCombinationResult => {
    // if(!orgDateTime &&  !newDateTime) return null;
    try {
        const orgDateTimeObj = orgDateTime instanceof Date ? orgDateTime : new Date(orgDateTime);
        if (!dayjs(orgDateTimeObj).isValid()) {
            const newDateAndTimeString = dayjs(newDateTime).toISOString();
            return newDateAndTimeString ? newDateAndTimeString : null;
        }
        const newDateTimeObj = newDateTime instanceof Date ? newDateTime : new Date(newDateTime);
        // if(!newDateTimeObj) return null;
        const orgDate = dayjs(orgDateTimeObj).format('YYYY-MM-DD');
        const newDate = dayjs(newDateTimeObj).format('YYYY-MM-DD');
        const orgTime = dayjs(orgDateTimeObj).format('HH:mm:ss');
        const newTime = dayjs(newDateTimeObj).format('HH:mm:ss');
        let combinedDateTime: string | Date = orgDateTimeObj;
        if (unit === 'date') {
            combinedDateTime = `${newDate}T${orgTime}`;
        } else {
            combinedDateTime = `${orgDate}T${newTime}`;
        }

        return dayjs(combinedDateTime).toISOString();
    } catch (e) {
        return '';
    }
};

export const combineDate = (originalDate: DateTime, newDate: DateTime): DateCombinationResult => {
    return combineDateTime(originalDate, newDate, 'date');
};

export const combineTime = (originalTime: DateTime, newTime: DateTime): DateCombinationResult => {
    return combineDateTime(originalTime, newTime, 'time');
};

export const getUpdatedValues = (obj1: StringAnyRecord, obj2: StringAnyRecord, keepUnmatchedKeys: boolean = false): StringAnyRecord => {
    const updatedValues: Record<string, any> = {};

    for (const key in obj2) {
        if (obj1.hasOwnProperty(key) && obj1[key] !== obj2[key]) {
            updatedValues[key] = obj2[key];
        } else if (!obj1.hasOwnProperty(key)) {
            updatedValues[key] = obj2[key];
        }
    }

    if (keepUnmatchedKeys) {
        for (const key in obj1) {
            if (!obj2.hasOwnProperty(key)) {
                updatedValues[key] = obj1[key];
            }
        }
    }

    return updatedValues;
};

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const format = (value: number) => {
    if (typeof value !== 'number') return '';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2, //  minimumFractionDigits and maximumFractionDigits ensures that the formatted number has exactly two decimal places.
        maximumFractionDigits: 2,
    }).format(value);
};

const sendTransactionTypes = new Set<string>([TransactionTypes.TRANSFER_TYPE]);

export const isSendMoneyTrx = (type: string | undefined): boolean => !!type && sendTransactionTypes.has(type);
export const formatAmount = (value: string | number = 0, amountType: 'dollar' | 'cents' = 'dollar', type?: string | undefined) => {
    const trxType = isSendMoneyTrx(type);
    if (!value) return format(0);
    let vText = String(value).replace('$', '').replace(',', '');

    let vValue = 0.0;
    if (vText) {
        vValue = +vText;
    }
    if (amountType === 'cents') {
        vValue = vValue / 100;
    }
    const sign = trxType ? '-' : '';
    return vValue < 0 ? format(vValue) : sign + format(vValue);
};

export const getFullName = (user: { firstName: string | undefined; lastName: string | undefined }) => {
    const { firstName, lastName } = user || {};
    if (!firstName && !lastName) return '';
    return `${firstName || ''} ${lastName || ''}`.trim();
};

export const getDateAndTime = (date: string) => {
    if (!date || !dayjs(date).isValid()) return '';
    return `${dateFormat(date) || ''} ${timeFormat(date) || ''}`.trim();
};

export const getAddress = (address?: { street1: string; street2: string } | null) => {
    if (!address) return '';
    const { street1, street2 } = address || {};
    return `${street1 || ''} ${street2 || ''}`.trim();
};

export const debounce = (callback: any, wait: number) => {
    let timeoutId: any = null;
    return (...args: any) => {
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
            callback(...args);
        }, wait);
    };
};

export const extractErrorMessage = (backendString: string): string | null => {
    if (!backendString) return '';
    const jsonMatch: RegExpMatchArray | null = backendString.match(/\{.*\}/);

    if (jsonMatch) {
        const jsonString: string = jsonMatch[0];

        try {
            const errorData: any = JSON.parse(jsonString);

            const errorMessage: string = errorData?.message || errorData?.error;

            return errorMessage;
        } catch (error) {
            console.error('Error parsing JSON:', error);
            return null;
        }
    } else {
        console.log('No JSON part found in the string.');
        return backendString;
    }
};

export const fetchAndPrepareImage = async (imageUrl: string) => {
    try {
        const response = await fetch(imageUrl);

        // Convert the fetched data into a Blob
        const blob = await response.blob();

        // Create a File object from the Blob
        const file = new File([blob], 'image.png', { type: 'image/png' });
        return file;
    } catch (error) {
        console.error('Error fetching image:', error);
        return null;
    }
};
export const getTodayFilter = () => {
    const startDate = dayjs().startOf('day');
    const endDate = dayjs().endOf('day');
    return {
        dayFrom: startDate.format(),
        dayTo: endDate.format(),
        value: 'today',
    };
};

export const getDefaultCompanyId = (state: RootState) => state.user.defaultCompanyId;

export const sortByField = (arr: Array<any>, sortByKey: string, order: 'ASC' | 'DESC' = 'ASC'): Array<any> => {
    if (!sortByKey) {
        return arr;
    }
    return arr.sort((a, b) => {
        let _a = get(a, sortByKey, '');
        let _b = get(b, sortByKey, '');
        if (order === 'ASC') {
            return _a > _b ? 1 : _b > _a ? -1 : 0;
        }
        return _b > _a ? 1 : _a > _b ? -1 : 0;
    });
};

/**
 * Finds values in an array of objects based on provided keys.
 * @param {Array} profileItems - The array of objects containing key value pair to search through.
 * @param {Array} keysToFind - The keys to search for in the array objects. It would be an array of strings
 * @returns {Object} - Object containing key-value pairs found in the array.
 */
export const findValuesByKeys = (profileItems: MerchantProfileItem[], keysToFind: Array<string>): object => {
    if (!profileItems?.length || !keysToFind?.length) {
        return {};
    }
    const uniqKeys = [...new Set(keysToFind)];
    if (!uniqKeys?.length) {
        return {};
    }
    return profileItems.reduce((result: any, item) => {
        if (keysToFind.includes(item.key)) {
            result[item.key] = item.value;
        }
        return result;
    }, {});
};

export const createQueryPayams = (obj: { [key: string]: any }): string => {
    const queryParams: string[] = [];
    for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
            queryParams.push(encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]));
        }
    }
    return queryParams.join('&');
};

const _get = (object: any, fieldPath: string) => {
    const path = _getPath(fieldPath);
    let pivot = object;
    while (path.length > 0) {
        if (!pivot) {
            return path.length > 0 ? undefined : pivot;
        }
        pivot = pivot[path.shift()];
    }
    return pivot;
};

/**
 * Gets a property value. This method is null safe.
 *
 * @param object the object to that contains the value.
 * @param fieldPath the field's path.
 * @param defaultValue the default value.
 * @return the value
 */
export const get = (object: any, fieldPath: string, defaultValue: any) => {
    const value = _get(object, fieldPath);
    if (defaultValue === undefined) {
        return value;
    }
    return isDefined(value) ? value : defaultValue;
};

export const isDefined = (value?: number | string | undefined) => value !== null && value !== undefined;

/**
 * @private
 * Used internally by the set method.
 */
const _getPath = (accessorString: string): any => {
    let path: any = [];
    const split = accessorString.split('.');
    split.forEach((field) => {
        const match = field.match(/\[\d+\]/g);
        if (!match) {
            path.push(field);
        } else if (!field.endsWith(']')) {
            throw new Error(`Invalid field name ${field}`);
        } else {
            let name = field;
            const indexes: any = [];
            match.forEach((chunk) => {
                name = name.replace(chunk, '');
                const num = chunk.match(/\d+/);
                indexes.push(Number(num && num[0]));
            });
            if (name.length > 0) {
                path = [...path, name, ...indexes];
            } else {
                path = [...path, ...indexes];
            }
        }
    });
    return path;
};

export const deletePropertyPath = (obj: any, path: string | string[]): void => {
    if (!obj || !path) {
        return;
    }

    if (typeof path === 'string') {
        path = path.split('.');
    }

    for (let i = 0; i < path.length - 1; i++) {
        obj = obj[path[i]];

        if (typeof obj === 'undefined') {
            return;
        }
    }

    delete obj[path.pop() as string];
};

//get blob url
export const getBlobUrl = (img: any) => {
    if (!img) return '';
    if (typeof img === 'string') return img;
    return URL?.createObjectURL(img);
};

export const camelToSpacedPascalCase = (str: string) => {
    return str
        .replace(/([A-Z])/g, ' $1') // Insert a space before each uppercase letter
        .replace(/^./, function (match) {
            return match.toUpperCase();
        }) // Capitalize the first letter
        .trim(); // Remove any leading space
};

export const getAttributesValue = (attributes: Attribute[], keyToFind: string) => {
    if (!attributes) return;
    const result = attributes.find((attr) => attr.key === keyToFind || attr.name === keyToFind)?.value;
    return result;
};

export const getDefaultContainerId = (moneyContainers: any) => {
    if (!Array.isArray(moneyContainers) || !moneyContainers) return;

    const item = moneyContainers.find((item: any) => item?.detail?.default);
    return item?.detail?.containerId;
};

export const getFromUsername = (trx: any) => {
    const { type } = trx || {};

    switch (type) {
        case 'PURCHASE':
            return trx?.consumer?.username || '';
        case 'M2M_PURCHASE':
            return trx?.consumer.username || '';
        case 'TRANSFER':
            return trx?.company.companyCode || '';
    }
};

export const getToUsername = (trx: any) => {
    const { type, company, user: receiverUser } = trx || {};
    const { user } = store.getState();
    switch (type) {
        case 'PURCHASE':
            return user.userInfo?.username;
        case 'M2M_PURCHASE':
            return company?.companyCode || '';
        case 'TRANSFER':
            return receiverUser?.username;
    }
};
export const calculateTrxTotal = (amount: number, transactionType: string, totalFee: number): string => {
    const sign = Math.sign(amount);
    const base = Math.abs(amount);
    const total = transactionType === 'm2p' ? base + totalFee : base - totalFee;
    return formatAmount(total * sign);
};

export const isM2PTrx = (type: TransactionType): boolean => type === 'TRANSFER';
export const isM2MTrx = (type: TransactionType): boolean => type === 'M2M_PURCHASE';

export const getTransactionType = (trx: { type?: string } | null | undefined): 'm2p' | 'p2m' | 'p2p' | 'm2m' | '' => {
    const type = trx?.type;

    if (isM2PTrx(type)) return 'm2p';
    if (isM2MTrx(type)) return 'm2m';
    return '';
};

export const showTransactionFee = (transactionType: string | undefined, myRole: any, transactionStatus: string): boolean => {
    const isCompleted = transactionStatus === 'COMPLETED';

    if (transactionType === 'm2p') {
        return myRole === 'SENDER' && isCompleted;
    }
    return myRole === 'RECEIVER' && isCompleted;
};

export const getUserInfoData = () => {
    return store.getState()?.user?.userInfo;
};

export const amISender = (trx: any) => {
    const userNickName = getUserInfoData()?.nickname || '';
    const fromNickName = getAttributesValue(trx?.attributes, 'FROM_NICKNAME') || '';
    return fromNickName === userNickName;
};

export const merchantHistoryData = (trx: any) => {
    const state = store.getState();
    const { userInfo } = state?.user || {};

    const transactionType = getTransactionType(trx);
    const myRole = amISender(trx) ? 'SENDER' : 'RECEIVER';
    const showFee = showTransactionFee(transactionType, myRole, trx?.transactionStatus);

    const filteredData: any = {
        amount: trx?.totalFee && showFee ? calculateTrxTotal(trx?.amount, transactionType, trx?.totalFee) : formatAmount(trx?.amount, 'dollar'),
        fromUsername: getAttributesValue(trx?.attributes, 'FROM_NICKNAME') || '',
        toUsername: getAttributesValue(trx?.attributes, 'TO_NICKNAME') || '',
        transactionType: getPaymentType(trx) || '',
        note: getAttributesValue(trx?.attributes, 'NOTE') || '',
    };
    if (trx?.totalFee && showFee) {
        filteredData.totalFee = trx?.totalFee;
    }
    if (trx?.transactionStatus === 'FAILED' || trx?.transactionStatus === 'ERROR') {
        filteredData.amount = format(0);
    }
    if (trx?.type === 'CREDIT') {
        filteredData.fromUsername = 'OGB Balance';
        filteredData.toUsername = userInfo?.nickname || '';
    } else if (trx?.type === 'DEBIT') {
        filteredData.fromUsername = userInfo?.nickname || '';
        filteredData.toUsername = 'OGB Balance';
    }

    return {
        ...trx,
        filteredData,
    };
};
export const isValidCurrencyString = (str: string) => {
    const validPattern = /^\$?[0-9]*$/;
    return validPattern.test(str);
};
export const formatInputAsCurrency = (value: string) => {
    if (!value) return 0;
    const vText = value?.replace('$', '').replace('.', '').replace(',', '');

    let vValue = 0.0;
    if (vText) {
        vValue = +vText / 100;
    }
    return '$' + vValue.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};
export const parseToFloatString = (value?: string) => {
    value = value || '0.00';
    value = value.replace(/[. $,-]/g, '');
    value = value.slice(0, value.length - 2) + '.' + value.slice(-2);
    if (value[0] === '0') value = value.slice(1);
    if (value[0] === '.') value = '0' + value;

    return value;
};

export const getAuthData = (): AuthData | null => {
    try {
        const authData = localStorage.getItem('token');
        if (!authData) return null;
        return JSON.parse(authData);
    } catch (err) {
        console.log('Error getting the access token', err);
    }
    return null;
};

export const getAccessToken = () => {
    try {
        const authData = getAuthData();
        if (authData) return authData.access_token;
        return null;
    } catch (err) {
        console.log('Error getting the access token', err);
    }
    return null;
};

export const getRoles = (accessToken: string) => {
    try {
        const authorities = parseJWT(accessToken)?.['authorities'] || [];
        const roles = Array.from(authorities.values()) as (keyof typeof ROLE)[];
        return roles;
    } catch (err) {
        console.log('Error getting the access token', err);
    }
    return null;
};

export const isMerchant = () => {
    const accessToken = getAccessToken();
    if (!accessToken) return false;
    return isOwner(getRoles(accessToken));
};

export const getPaymentType = (trx: { transactionStatus: string; attributes: any }): string => {
    const { transactionStatus, attributes } = trx;
    const { user } = store.getState();
    const fromNickname = getAttributesValue(attributes, 'FROM_NICKNAME');
    if (transactionStatus === 'COMPLETED') {
        if (fromNickname?.toLowerCase() === user?.userInfo?.nickname?.toLowerCase()) {
            return 'Payment sent';
        } else {
            return 'Payment received';
        }
    }

    if (transactionStatus === 'FAILED' || transactionStatus === 'ERROR') {
        return 'Transaction failed';
    }

    return '';
};

export const isInvoiceReceiver = (role: string) => role === 'SENDER';

export const getName = (data: any, fieldFor: 'TO' | 'FROM' = 'FROM') => {
    const { receiverCompany, receiverUser, senderCompany, senderUser } = data || {};

    if (fieldFor === 'TO') {
        return senderCompany?.name || senderUser?.name || '';
    }
    return receiverUser?.name || receiverCompany?.name || '';
};

export const getRequestTransactionDetails = (trx: RequestMoneyDetailTypes) => {
    if (!trx) return null;
    const { amount, requestMoneyId, statusDate, subject, myRole = '', company, companySender, userSender, user, status } = trx;

    let details: RequestMoneyDetailTypes = {
        confirmationCode: requestMoneyId,
        transactionDateTime: statusDate,
        balance: amount,
        note: subject,
        status,
        myRole,
    };
    const isReceiver = isInvoiceReceiver(myRole);
    if (!isReceiver) {
        const { name, imgUrl, username } = userSender || {};
        const { name: companyName, companyCode: companyNickName, logoUrl } = companySender || {};

        details = {
            ...details,
            name: name || companyName,
            logoUrl: imgUrl || logoUrl,
            username: username || companyNickName,
        };
    } else {
        const { name, imgUrl, username } = user || {};
        const { name: companyName, companyCode: companyNickName, logoUrl } = company || {};
        details = {
            ...details,
            name: name || companyName,
            logoUrl: imgUrl || logoUrl,
            username: username || companyNickName,
        };
    }
    return details;
};

export const getAccountActivityTrxDetail = (trx: TransactionDetail) => {
    if (!trx) return null;

    const { amount, transactionDateTime, transactionStatus, subject, consumer, company, user, type, filteredData, confirmationCode } = trx;
    const { userInfo } = store.getState().user;
    const userNickname = userInfo?.nickname;

    const titleFrom = filteredData?.fromUsername;
    const trxStatus = transactionStatus === 'ERROR' ? 'FAILED' : transactionStatus;
    let title = 'From';
    if (titleFrom === userNickname) {
        title = 'To';
    }

    let details: FilterTransactionDetails = {
        title,
        balance: filteredData?.amount,
        totalFee: filteredData?.totalFee,
        note: subject || filteredData?.note,
        status: trxStatus,
        transactionDateTime,
        type: filteredData?.transactionType,
        transactionAmount: amount,
        trxId: confirmationCode,
    };
    // For payment received (M2M and P2M), we need to display consumer data when the title is 'From'.
    if (consumer && title === 'From') {
        const { name, username, imgUrl } = consumer || {};
        details = {
            ...details,
            name,
            username,
            logoUrl: imgUrl,
        };
    } else if (title === 'To' && !user) {
        // For M2M send money, when the title is 'To', we need to display company data and set user to false, as there are other cases where both user and company are present.
        const { name, companyCode, logoUrl } = company || {};
        details = {
            ...details,
            name,
            username: companyCode,
            logoUrl,
        };
    } else if (user) {
        //For M2P send money, we need to display the user. Although the company is also present, it does not affect this condition due to the above condition.
        const { name, username, imgUrl } = user || {};
        details = {
            ...details,
            name,
            username,
            logoUrl: imgUrl,
        };
    } else if (type === 'CREDIT') {
        details = {
            ...details,
            name: filteredData?.fromUsername,
        };
    } else if (type === 'DEBIT') {
        details = {
            ...details,
            name: filteredData?.toUsername,
        };
    }

    return details;
};
export const parseCSV = (csv: string): ParseCSVResponse => {
    // Find header start index
    const lines = csv.trim().split('\n');
    const startIndex = lines.findIndex((line: string) => line.startsWith('Date,'));
    const csvData = lines.slice(startIndex).join('\n');

    const result = Papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
    }) as unknown as ParseCSVResponse;

    return result;
};

export const formatDateAndTime = (date: string) => {
    return dayjs(date).format();
};

export const formatNameWithNickname = (name: string | undefined, nickname: string | undefined) => {
    if (!name && !nickname) return '';
    const formattedName = name || '';
    const formattedNickname = nickname ? ` (${nickname})` : '';
    return (formattedName + formattedNickname).trim();
};

export const getInitials = (name: string) => {
    const namesArray = name.trim().split(' ');
    if (namesArray.length > 1) {
        const firstInitial = namesArray[0][0];
        const lastInitial = namesArray[namesArray.length - 1][0];
        return (firstInitial + lastInitial).toUpperCase();
    } else {
        return namesArray[0][0].toUpperCase();
    }
};

export const handleCopyClipBoard = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
};

export const userNameSchema = yup
    .string()
    .required('Username is required')
    .test('not-email', 'Username cannot be an email', (value) => !value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
    .min(6, 'Username must be at least 6 characters long')
    .max(22, 'Username cannot exceed 22 characters')
    .matches(/^[^\s]+$/, 'Username cannot contain spaces');

export const showCopiedToast = (message: string = 'Copied!') => {
    toast(message, {
        closeButton: false,
        position: 'bottom-center',
        hideProgressBar: true,
        theme: 'light',
        icon: false,
        toastId: 'copied-toast',
        style: {
            borderRadius: '50px',
            width: '200px',
            textAlign: 'center',
        },
    });
};

export const generateUUIDv4 = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c: string) => {
        const r: number = (Math.random() * 16) | 0;
        const v: number = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

export const hashSHA256 = async (str: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer: ArrayBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray: number[] = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b: number) => b.toString(16).padStart(2, '0')).join('');
};

export const getDeviceId = async (): Promise<string> => {
    let deviceId = localStorage.getItem(deviceIdKey);
    if (!deviceId) {
        const envInfo = [
            navigator.userAgent,
            navigator.language,
            Intl.DateTimeFormat().resolvedOptions().timeZone,
            generateUUIDv4(), // Add randomness
        ].join('|');

        deviceId = await hashSHA256(envInfo);
        localStorage.setItem(deviceIdKey, deviceId);
    }

    return deviceId;
};
export const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,20}$/;
export const PASSWORD_SCHEMA = yup.string().required('Password is required').matches(PASSWORD_REGEX, {
    message: 'Password must be 8–20 characters and include uppercase, lowercase, digit, and special character.',
});
